import { useCallback, useEffect, useState } from "react";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, primaryButtonSx } from "./consoleTheme";
import { indiaDate } from "../../include/indiaDate";

const EVENTS = [
  { id: "", label: "All events" },
  { id: "sale_register", label: "Sale register" },
  { id: "loyalty_unlock", label: "Loyalty coupon unlock" },
  { id: "draw_winner", label: "Draw winner" },
  { id: "store_register", label: "Store register" },
];
const CHANNELS = [
  { id: "", label: "All channels" },
  { id: "email", label: "Email" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "sms", label: "SMS" },
  { id: "notification", label: "Notification" },
];

function eventLabel(id) {
  return EVENTS.find((item) => item.id === id)?.label || id || "—";
}

function channelLabel(id) {
  return CHANNELS.find((item) => item.id === id)?.label || id || "—";
}

function toLine(row) {
  return [row.to_name, row.to_mobile, row.to_email, row.store_name].filter(Boolean).join(" · ") || "—";
}

export default function CommLogs() {
  const [rows, setRows] = useState([]);
  const [event, setEvent] = useState("");
  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = useCallback(async () => {
    const result = await adminPost("COMM_LOGS", { event, channel, status, startDate, endDate, start: 0, limit: 300 });
    if (result.status === "200" && Array.isArray(result.data)) setRows(result.data);
    else toast.error(result?.status_message || "Could not load communication logs");
  }, [event, channel, status, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center", background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 1.5 }}>
        <TextField select size="small" label="Event" value={event} onChange={(e) => setEvent(e.target.value)} sx={{ minWidth: 180 }}>
          {EVENTS.map((item) => <MenuItem key={item.id || "all"} value={item.id}>{item.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Channel" value={channel} onChange={(e) => setChannel(e.target.value)} sx={{ minWidth: 160 }}>
          {CHANNELS.map((item) => <MenuItem key={item.id || "all"} value={item.id}>{item.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="sent">Sent</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        <Button variant="contained" onClick={load} sx={primaryButtonSx}>Apply</Button>
      </Box>

      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, overflow: "hidden" }}>
        {rows.map((row) => {
          const sent = row.status === "sent";
          return (
            <Box key={row.log_id} sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${consoleColors.line}` }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: consoleColors.ink }}>
                  {eventLabel(row.event)} · {channelLabel(row.channel)}
                  {row.template_name ? ` · ${row.template_name}` : ""}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: sent ? "#15803d" : "#b91c1c" }}>
                  {sent ? "Sent" : "Failed"}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.4 }}>
                {indiaDate(row.date_added)} · {toLine(row)}
                {row.reference ? ` · ${row.reference}` : ""}
              </Typography>
              <Typography
                sx={{
                  mt: 0.8,
                  fontSize: 13.5,
                  color: sent ? consoleColors.muted : consoleColors.ink,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  lineHeight: 1.45,
                }}
              >
                {row.reason || (sent ? "Sent" : "No reason recorded")}
              </Typography>
            </Box>
          );
        })}
        {!rows.length ? (
          <Typography sx={{ p: 2, color: consoleColors.muted }}>No communication logs for these filters.</Typography>
        ) : null}
      </Box>
    </Box>
  );
}
